import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, Plus, RefreshCw, CheckCircle2, Clock, AlertTriangle, Info,
} from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    no_leida: "bg-blue-50 text-blue-700 border-blue-200",
    leida: "bg-slate-100 text-slate-600 border-slate-200",
    respondida: "bg-green-50 text-green-700 border-green-200",
  };
  const labels = { no_leida: "No leida", leida: "Leida", respondida: "Respondida" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${map[status] || map.no_leida}`}>
      {labels[status] || status}
    </span>
  );
}

function TypeBadge({ type }) {
  const map = {
    requerimiento: "bg-red-50 text-red-700 border-red-200",
    notificacion_general: "bg-slate-100 text-slate-600 border-slate-200",
    obligacion_pendiente: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const labels = {
    requerimiento: "Requerimiento",
    notificacion_general: "Notificacion",
    obligacion_pendiente: "Obligacion",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${map[type] || map.notificacion_general}`}>
      {labels[type] || type}
    </span>
  );
}

function AddNotificationModal({ entities, onSuccess, onClose }) {
  const [form, setForm] = useState({
    legal_entity_id: entities[0]?.id || "",
    notification_type: "notificacion_general",
    title: "",
    body_text: "",
    expires_at: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("El titulo es obligatorio");
    setLoading(true);
    try {
      await api.post("/ops/fiscal/sat-notifications", form);
      toast.success("Notificacion registrada");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
        <h3 className="font-bold text-slate-900 mb-4">Registrar notificacion del SAT</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Entidad legal</label>
            <select
              data-testid="notif-entity-select"
              value={form.legal_entity_id}
              onChange={e => setForm(f => ({ ...f, legal_entity_id: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {entities.map(en => <option key={en.id} value={en.id}>{en.legal_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Tipo</label>
            <select
              data-testid="notif-type-select"
              value={form.notification_type}
              onChange={e => setForm(f => ({ ...f, notification_type: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="notificacion_general">Notificacion general</option>
              <option value="requerimiento">Requerimiento</option>
              <option value="obligacion_pendiente">Obligacion pendiente</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Titulo</label>
            <input
              data-testid="notif-title-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Ej: Requerimiento de informacion 2026"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Descripcion (opcional)</label>
            <textarea
              data-testid="notif-body-input"
              value={form.body_text}
              onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Fecha de vencimiento (opcional)</label>
            <input
              data-testid="notif-expires-input"
              type="date"
              value={form.expires_at}
              onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              data-testid="btn-guardar-notificacion"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading && <RefreshCw className="w-3 h-3 animate-spin" />}
              Guardar
            </button>
            <button type="button" onClick={onClose} className="border border-slate-300 rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function OpsFiscalBuzon() {
  const [notifications, setNotifications] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [markingRead, setMarkingRead] = useState(null);

  const load = useCallback(async () => {
    try {
      const [notifRes, entRes] = await Promise.all([
        api.get("/ops/fiscal/sat-notifications"),
        api.get("/ops/legal-entities"),
      ]);
      setNotifications(notifRes.data.notifications || []);
      const ents = entRes.data;
      setEntities(Array.isArray(ents) ? ents : ents.entities || ents.legal_entities || []);
    } catch (e) {
      toast.error("Error al cargar buzon tributario");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkRead = async (notifId) => {
    setMarkingRead(notifId);
    try {
      await api.post(`/ops/fiscal/sat-notifications/${notifId}/mark-read`);
      toast.success("Marcada como leida");
      load();
    } catch (e) {
      toast.error("Error al marcar como leida");
    } finally {
      setMarkingRead(null);
    }
  };

  const unread = notifications.filter(n => n.status === "no_leida").length;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Buzon Tributario SAT
            {unread > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">{unread}</span>
            )}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Notificaciones del SAT — registro manual</p>
        </div>
        <button
          data-testid="btn-nueva-notificacion"
          onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Registrar notificacion
        </button>
      </div>

      {/* Aviso sobre automatizacion */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-2">
        <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Lectura automatica del Buzon no disponible aun</p>
          <p className="text-xs text-amber-700 mt-0.5">
            La lectura automatica del Buzon Tributario requiere la e.firma (FIEL) del contribuyente.
            Por ahora, registra las notificaciones manualmente copiando el contenido del portal del SAT.
            La automatizacion se activara cuando el cliente entregue la e.firma al equipo de Emergent.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />Cargando...
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Buzon vacio</p>
          <p className="text-sm text-slate-400 mt-1">
            Registra las notificaciones del SAT manualmente para hacer seguimiento
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map(notif => (
            <div
              key={notif.id}
              data-testid={`notif-row-${notif.id}`}
              className={`bg-white border rounded-xl p-5 shadow-sm transition-all ${
                notif.status === "no_leida" ? "border-blue-200" : "border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-semibold text-slate-800 text-sm">{notif.title}</p>
                    <TypeBadge type={notif.type} />
                    <StatusBadge status={notif.status} />
                  </div>
                  {notif.body_text && (
                    <p className="text-xs text-slate-600 mt-1">{notif.body_text}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    <span>Recibida: {notif.received_at?.slice(0, 10)}</span>
                    {notif.expires_at && <span className="text-red-500">Vence: {notif.expires_at?.slice(0, 10)}</span>}
                  </div>
                </div>
                {notif.status === "no_leida" && (
                  <button
                    data-testid={`btn-marcar-leida-${notif.id}`}
                    onClick={() => handleMarkRead(notif.id)}
                    disabled={markingRead === notif.id}
                    className="text-xs border border-green-200 text-green-700 rounded-lg px-3 py-1.5 hover:bg-green-50 flex items-center gap-1 shrink-0"
                  >
                    {markingRead === notif.id
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <CheckCircle2 className="w-3 h-3" />
                    }
                    Marcar leida
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <AddNotificationModal
          entities={entities}
          onSuccess={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
